import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { money } from "../lib/data";

const STATUS_LABEL = {
  pending_payment: "AWAITING PAYMENT",
  paid: "ORDER CONFIRMED",
  production: "IN PRODUCTION",
  shipped: "SHIPPED",
  delivered: "DELIVERED",
  cancelled: "CANCELLED",
  refunded: "REFUNDED",
  payment_failed: "PAYMENT FAILED",
};

function OrderCard({ order }) {
  return (
    <div data-testid="order-card" className="border border-ll-line bg-ll-coal p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-tech text-[11px] tracking-[0.2em] text-ll-steel">ORDER</p>
          <p data-testid="order-number" className="font-display text-3xl tracking-wide">{order.order_number}</p>
        </div>
        <span data-testid="order-status-badge" className="bg-ll-red px-3 py-1 font-tech text-[11px] tracking-[0.2em]">
          {STATUS_LABEL[order.status] || order.status.toUpperCase()}
        </span>
      </div>
      <p className="mt-2 font-tech text-[11px] tracking-[0.15em] text-ll-steel">{order.tag}</p>

      <div className="mt-6 space-y-3">
        {order.items.map((i) => (
          <div key={i.slug || i.sku} className="flex items-center justify-between border-b border-ll-line pb-3">
            <div>
              <p className="text-sm font-semibold">{i.name || i.title}</p>
              <p className="font-tech text-[11px] tracking-[0.15em] text-ll-steel">QTY {i.quantity}</p>
            </div>
            <p className="font-tech text-sm">{money(i.unit_price * i.quantity)}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 space-y-1 font-tech text-xs text-ll-steel">
        <p>ESTIMATED SHIPPING: <span className="text-ll-bone">{order.ship_window}</span></p>
        {order.amount_total && <p>TOTAL PAID: <span className="text-ll-bone">{money(order.amount_total)}</span></p>}
        <p>PLACED: <span className="text-ll-bone">{new Date(order.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span></p>
      </div>
      <p className="mt-4 border border-ll-linestrong bg-ll-black px-3 py-2 text-[11px] text-ll-steel">
        Shipping and delivery updates are sent to {order.customer.email}.
      </p>
    </div>
  );
}

export default function OrderStatus() {
  const { token } = useParams();
  const [form, setForm] = useState({ order_number: "", email: "" });
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token) {
      setLoading(true);
      api.get(`/orders/track/${token}`)
        .then((r) => setOrder(r.data))
        .catch(() => setError("This order link is invalid or has expired."))
        .finally(() => setLoading(false));
    }
  }, [token]);

  const lookup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.get("/orders/lookup", { params: form });
      setOrder(data);
    } catch (err) {
      setError(err.response?.data?.detail || "No order found.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="order-status-page" className="mx-auto max-w-3xl px-5 pb-24 pt-32 md:pt-40">
      <p className="font-tech text-[11px] tracking-[0.3em] text-ll-red">GUEST LOOKUP</p>
      <h1 className="mt-2 font-display text-6xl tracking-wide md:text-7xl">ORDER STATUS</h1>

      {loading && <p data-testid="order-loading" className="mt-10 font-tech text-sm text-ll-steel">Loading…</p>}
      {error && <p data-testid="order-error" className="mt-10 border border-ll-red/50 bg-ll-red/10 px-4 py-3 text-sm text-ll-red">{error}</p>}
      {order && <div className="mt-10"><OrderCard order={order} /></div>}

      {!token && !order && (
        <form onSubmit={lookup} className="mt-10 space-y-5" noValidate>
          <p className="text-sm text-ll-steel">Enter the order number and email from your confirmation.</p>
          <div>
            <label htmlFor="os-number" className="font-tech text-[11px] tracking-[0.2em] text-ll-steel">ORDER NUMBER</label>
            <input id="os-number" data-testid="order-number-input" type="text" value={form.order_number}
                   onChange={(e) => setForm({ ...form, order_number: e.target.value })}
                   placeholder="LL-XXXXXX"
                   className="mt-1 w-full border border-ll-linestrong bg-ll-black px-4 py-3 text-sm placeholder:text-ll-steel/60 outline-none transition-colors duration-200 focus:border-ll-red" />
          </div>
          <div>
            <label htmlFor="os-email" className="font-tech text-[11px] tracking-[0.2em] text-ll-steel">EMAIL</label>
            <input id="os-email" data-testid="order-email-input" type="email" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="mt-1 w-full border border-ll-linestrong bg-ll-black px-4 py-3 text-sm outline-none transition-colors duration-200 focus:border-ll-red" />
          </div>
          <motion.button data-testid="order-lookup-button" whileTap={{ scale: 0.98 }} type="submit" disabled={loading}
                         className="bg-ll-red px-10 py-4 font-tech text-sm tracking-[0.25em] transition-colors duration-200 hover:bg-ll-redhover disabled:opacity-60">
            FIND MY ORDER
          </motion.button>
        </form>
      )}
    </div>
  );
}
